import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api, type EmailCodeStatus } from '../api/client';
import { translateErrorCode } from '../i18n';
import { cuentaRegresiva } from '../session/inactividad';
import { urlDeAcceso } from '../session/acceso';

/**
 * El código por correo: la otra forma de terminar de entrar para quien
 * administra y no trae su teléfono.
 *
 * Se llega desde la pantalla del código de Keycloak («recibir el código por
 * correo»), con el acceso a medias: la contraseña ya se escribió y todavía no hay
 * sesión. Por eso esta pantalla vive fuera de la aplicación, como la portada.
 *
 * Al abrirse pide el código si todavía no se mandó ninguno: quien llegó aquí es
 * porque lo quiere, y un botón más de «enviar» sólo sería un paso más.
 */
export function VerificacionPorCorreo() {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<EmailCodeStatus | null>(null);
  /** Cuánto va adelantado (o atrasado) este reloj respecto al del servidor. */
  const [desfase, setDesfase] = useState(0);
  const recibir = useCallback((nuevo: EmailCodeStatus) => {
    setEstado(nuevo);
    setDesfase(Date.parse(nuevo.now) - Date.now());
  }, []);
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  /** El acceso pendiente ya no existe: venció o se agotaron los intentos. */
  const [terminado, setTerminado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    const reloj = window.setInterval(() => setAhora(Date.now()), 1_000);
    return () => window.clearInterval(reloj);
  }, []);

  const fallo = useCallback(
    (e: unknown) => {
      if (
        e instanceof ApiError &&
        (e.status === 401 ||
          e.code === 'auth.email_code_exhausted' ||
          e.code === 'auth.second_step_expired')
      ) {
        // Agotó los intentos, o el acceso pendiente ya no existe: en los dos casos
        // hay que volver a escribir la contraseña.
        setTerminado(
          translateErrorCode(
            e.code === 'auth.email_code_exhausted' ? e.code : 'auth.second_step_expired',
          ),
        );
        return;
      }
      setError(
        e instanceof ApiError ? translateErrorCode(e.code, e.params) : translateErrorCode('internal.error'),
      );
    },
    [],
  );

  const enviar = useCallback(async () => {
    setOcupado(true);
    setError(null);
    setAviso(null);
    try {
      recibir(await api.emailCodeSend());
      setAviso(t('verification.sent'));
    } catch (e) {
      fallo(e);
    } finally {
      setOcupado(false);
    }
  }, [fallo, recibir, t]);

  useEffect(() => {
    let vigente = true;
    api
      .emailCodeStatus()
      .then((actual) => {
        if (!vigente) return;
        recibir(actual);
        if (!actual.expiresAt) void enviar();
      })
      .catch((e) => vigente && fallo(e));
    return () => {
      vigente = false;
    };
  }, [enviar, fallo, recibir]);

  async function verificar(evento: React.FormEvent) {
    evento.preventDefault();
    setOcupado(true);
    setError(null);
    setAviso(null);
    try {
      const { returnTo } = await api.emailCodeVerify(codigo);
      // Recarga completa: la aplicación arranca ya con la sesión, como después
      // de cualquier otro acceso.
      window.location.replace(returnTo || '/');
    } catch (e) {
      fallo(e);
      setCodigo('');
      setOcupado(false);
    }
  }

  const ahoraServidor = ahora + desfase;
  const vence = estado?.expiresAt ? Date.parse(estado.expiresAt) - ahoraServidor : null;
  const reenviarEn = estado?.canResendAt ? Date.parse(estado.canResendAt) - ahoraServidor : 0;
  const puedeReenviar = Boolean(estado) && estado!.sendsLeft > 0 && reenviarEn <= 0 && !ocupado;

  return (
    <div className="acceso acceso--sola">
      <section className="acceso__tarjeta">
        <span className="acceso__institucion">{t('app.institution')}</span>
        <span className="acceso__sello" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.4">
            <rect x="6" y="11" width="36" height="26" rx="2" />
            <path d="m7 13 17 13 17-13" />
          </svg>
        </span>
        <h1 className="acceso__tarjeta-titulo">{t('verification.title')}</h1>

        {terminado ? (
          <>
            <p className="aviso aviso--error" role="alert" style={{ width: '100%' }}>
              {terminado}
            </p>
            <a className="boton boton--grande acceso__boton" href={urlDeAcceso()}>
              {t('verification.startOver')}
            </a>
          </>
        ) : (
          <>
            <p className="acceso__tarjeta-texto">
              {estado
                ? t('verification.explanation', { email: estado.maskedEmail })
                : t('app.loading')}
            </p>

            {aviso && !error && (
              <p className="aviso aviso--exito" role="status" style={{ width: '100%' }}>
                {aviso}
              </p>
            )}
            {error && (
              <p className="aviso aviso--error" role="alert" style={{ width: '100%' }}>
                {error}
              </p>
            )}

            <form className="verificacion" onSubmit={(e) => void verificar(e)}>
              <label className="verificacion__etiqueta" htmlFor="codigo">
                {t('verification.codeLabel')}
              </label>
              <input
                id="codigo"
                className="verificacion__codigo mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9 ]*"
                maxLength={7}
                autoFocus
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/[^0-9 ]/g, ''))}
                aria-describedby="codigo-vigencia"
              />
              <span id="codigo-vigencia" className="campo__ayuda">
                {vence === null
                  ? ''
                  : vence > 0
                    ? t('verification.expiresIn', { tiempo: cuentaRegresiva(vence) })
                    : t('verification.expired')}
              </span>
              <button
                className="boton boton--grande acceso__boton"
                type="submit"
                disabled={ocupado || codigo.replace(/\s/g, '').length !== 6}
              >
                {t('verification.verify')}
              </button>
            </form>

            <div className="verificacion__opciones">
              <button
                type="button"
                className="boton boton--discreto"
                disabled={!puedeReenviar}
                onClick={() => void enviar()}
              >
                {reenviarEn > 0
                  ? t('verification.resendIn', { tiempo: cuentaRegresiva(reenviarEn) })
                  : t('verification.resend', { count: estado?.sendsLeft ?? 0 })}
              </button>
              <a className="acceso__enlace" href="/api/v1/auth/second-factor/app">
                {t('verification.useAuthenticator')}
              </a>
            </div>

            <p className="acceso__nota">{t('verification.notice')}</p>
          </>
        )}
      </section>
    </div>
  );
}
